import { useCallback, useEffect, useRef, useState } from "react";
import io from "socket.io-client";

import { getPixAndGoStatus } from "@/services/pixAndGoApi";

const BACKOFF_SCHEDULE_MS = [5000, 10000, 20000, 40000, 60000];
const MAX_ATTEMPTS = 8;

function usePixAndGoStatusSync(
  uuid,
  { onStatus, onExhausted, autoStart = false, gracePeriodMs = 10000 } = {},
) {
  const [isChecking, setIsChecking] = useState(false);
  const timeoutRef = useRef(null);
  const attemptRef = useRef(0);
  const resolvedRef = useRef(false);
  const runningRef = useRef(false);

  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;
  const onExhaustedRef = useRef(onExhausted);
  onExhaustedRef.current = onExhausted;

  const stop = useCallback(() => {
    resolvedRef.current = true;
    runningRef.current = false;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
    setIsChecking(false);
  }, []);

  const poll = useCallback(() => {
    if (resolvedRef.current || !uuid) return;
    getPixAndGoStatus(uuid)
      .then((res) => {
        if (!resolvedRef.current) onStatusRef.current?.(res.data.status);
      })
      .catch(() => {
        // transient network error — keep retrying per schedule
      })
      .finally(() => {
        if (resolvedRef.current) return;
        attemptRef.current += 1;
        if (attemptRef.current >= MAX_ATTEMPTS) {
          runningRef.current = false;
          setIsChecking(false);
          onExhaustedRef.current?.();
          return;
        }
        const delay =
          BACKOFF_SCHEDULE_MS[
            Math.min(attemptRef.current - 1, BACKOFF_SCHEDULE_MS.length - 1)
          ];
        timeoutRef.current = setTimeout(poll, delay);
      });
  }, [uuid]);

  const checkNow = useCallback(() => {
    if (resolvedRef.current || runningRef.current || !uuid) return;
    runningRef.current = true;
    attemptRef.current = 0;
    setIsChecking(true);
    poll();
  }, [poll, uuid]);

  useEffect(() => {
    if (!uuid) return undefined;
    resolvedRef.current = false;

    const socket = io(process.env.REACT_APP_SERVER_URL, {
      path: "/websocket/socket.io",
    });

    socket.on("confirmTransaction", (socketData) => {
      if (socketData.event !== "pix_and_go" || socketData.ulid !== uuid) return;
      stop();
      onStatusRef.current?.(socketData.status);
    });

    let graceTimer;
    if (autoStart) {
      graceTimer = setTimeout(() => checkNow(), gracePeriodMs);
    }

    return () => {
      stop();
      socket.disconnect();
      clearTimeout(graceTimer);
    };
  }, [uuid, autoStart, gracePeriodMs, checkNow, stop]);

  return { checkNow, isChecking, resolve: stop };
}

export default usePixAndGoStatusSync;
